import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Scissors } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminReconcileTierCutsButton — Round 95g (Feb 2026).
 *
 * One-shot cleanup for the `tier_list_cuts` collection that feeds the
 * dashboard's "Cut From List" card. Retroactively applies the same
 * filters the new write-path already uses, so historically-stored
 * false positives disappear:
 *   • ETFs (SCHD / SPY / VYM etc.) — deactivated (they never belonged
 *     on the curated corporate list, so their removal isn't a "cut").
 *   • Share-class ticker normalization (BF.B ↔ BF-B, AGM.A ↔ AGM-A,
 *     WSO.B ↔ WSO-B) — deactivated when the normalized ticker IS on
 *     the curated list under the dash form.
 *   • Foreign F/Y suffix OTC / ADRs — soft-tagged as `foreign_adr` so
 *     the frontend renders them as amber "FX?" chips instead of red
 *     cut chips.
 *
 * Idempotent — safe to click any time.
 */
export default function AdminReconcileTierCutsButton() {
  const { user } = useAuth();
  const [firing, setFiring] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (user?.role !== 'admin') return null;

  const handleFire = async () => {
    if (firing) return;
    setFiring(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/tier-cuts/reconcile`,
        {},
        { withCredentials: true, timeout: 180 * 1000 },
      );
      setLastResult(data);
      toast.success('Streaks reconciled (calendar-year math)', {
        description: `Evaluated ${data.evaluated} · New cuts: ${data.cut?.length || 0} · Restored to lists: ${data.restored?.length || 0} · At risk: ${data.at_risk?.length || 0} · Cuts remaining: ${data.active_cuts_remaining}`,
      });
    } catch (err) {
      toast.error('Reconcile failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setFiring(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleFire}
        disabled={firing}
        data-testid="admin-reconcile-tier-cuts-fire"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: re-judge every King / Aristocrat / Achiever and every 'Cut from List' row with calendar-year payout math (split-adjusted, specials removed). Confirmed breaks move to Cut from List; intact streaks are restored to their lists; Streaks at Risk refreshes. Same job the 9 AM ET daily cron runs."
      >
        {firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
        Reconcile Streaks & Cuts
      </button>
      {lastResult && (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300"
          data-testid="admin-reconcile-tier-cuts-status"
          title={`New cuts: ${(lastResult.cut || []).map((c) => c.ticker).join(', ') || '—'}\nRestored: ${(lastResult.restored || []).map((r) => r.ticker).join(', ') || '—'}\nAt risk: ${(lastResult.at_risk || []).join(', ') || '—'}\nCuts remaining: ${lastResult.active_cuts_remaining}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          {lastResult.active_cuts_remaining} cut · {(lastResult.restored || []).length} restored
        </span>
      )}
    </div>
  );
}
